import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { serializeAxiosError } from '../../../../../Config/Reducers/ReducerUtils';
import { RequestError } from '../../../../../Config/Types';

export interface EquipmentType {
    id: number;
    name: string;
    displayName: string;
    sortPriority: number;
}

export interface AlarmSeverity {
    id: number;
    name: string;
    displayName: string;
    sortPriority: number;
}

export interface SideBarFilterState {
    loading: boolean;
    saving: boolean;

    errorMessage: string | undefined;
    successMessage: string | undefined;
    equipments: EquipmentType[];
    severities: AlarmSeverity[];
}

const initialState: SideBarFilterState = {
    loading: false,
    saving: false,
    errorMessage: undefined,
    successMessage: undefined,
    equipments: [],
    severities: [],
};

export const fetchEquipments = createAsyncThunk<EquipmentType[]>(
    'sideBarFilter/fetchEquipments',
    async () => {
        const response = await axios.get('/api/equipments/find-all');
        return response.data?.data || [];
    },
    { serializeError: serializeAxiosError }
);

export const fetchSeverities = createAsyncThunk<AlarmSeverity[]>(
    'sideBarFilter/fetchSeverities',
    async () => {
        const response = await axios.get('/api/alarmSeverity/find-all');
        const data = [...response.data?.data || []];
        return data;
    },
    { serializeError: serializeAxiosError }
);

export const fetchFiltersData = createAsyncThunk<
    { equipments: EquipmentType[]; severities: AlarmSeverity[] }
>(
    'sideBarFilter/fetchFiltersData',
    async () => {
        const [equipmentRes, severityRes] = await Promise.all([
            axios.get('/api/equipments/find-all'),
            axios.get('/api/alarmSeverity/find-all'),
        ]);
        severityRes.data?.data.map((severity: AlarmSeverity) => {
            if (severity.name === 'Crítica') {
                severity.displayName = 'Crítica';
                severity.name = 'critical_with_tele_object'
            }
            return severity;
        });
        const data = [...severityRes.data?.data || []];

        return {
            equipments: equipmentRes.data?.data || [],
            severities: data,
        };
    },
    { serializeError: serializeAxiosError }
);

const sideBarFilterReducer = createSlice({
    name: 'sideBarFilter',
    initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Filters Data
            .addCase(fetchFiltersData.pending, (state) => {
                state.loading = true;
                state.errorMessage = undefined;
            })
            .addCase(fetchFiltersData.fulfilled, (state, action) => {
                state.loading = false;
                state.equipments = action.payload.equipments;
                state.severities = action.payload.severities;
            })
            .addCase(fetchFiltersData.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = (action.payload as RequestError)?.message || 'Erro ao carregar filtros';
            })

            // Fetch Equipments only
            .addCase(fetchEquipments.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchEquipments.fulfilled, (state, action) => {
                state.loading = false;
                state.equipments = action.payload;
            })
            .addCase(fetchEquipments.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = (action.payload as RequestError)?.message || 'Erro ao carregar equipamentos';
            })

            // Fetch Severities only
            .addCase(fetchSeverities.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSeverities.fulfilled, (state, action) => {
                
                state.loading = false;
                state.severities = action.payload;
            })
            .addCase(fetchSeverities.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = (action.payload as RequestError)?.message || 'Erro ao carregar severidades';
            });
    },
});

export const { reset } = sideBarFilterReducer.actions;
export default sideBarFilterReducer.reducer;
